// Billing endpoints (admin JWT). Shapes mirror the API:
//   - Invoice → api/src/services/billing-service.ts (Invoice)
//   - routes  → api/src/http/routes/billing.ts (POST /billing/checkout, GET /billing/invoices)
//
// Invoice success bodies are Zod-validated at the boundary (vault Type_Assertion).
// `amount` is in the currency's MINOR units (e.g. pence) — divide by 100 to display.
import { z } from "zod";
import { apiFetch } from "./client";
import type { TierId } from "./auth";

export type PaidTierId = Exclude<TierId, "free">;

export interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amount: number;
  currency: string;
  created: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

const invoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
  created: z.string(),
  hostedInvoiceUrl: z.string().nullable(),
  invoicePdf: z.string().nullable(),
}) satisfies z.ZodType<Invoice>;

const invoicesResponseSchema = z.object({ invoices: z.array(invoiceSchema) });
const checkoutResponseSchema = z.object({ url: z.string().min(1) });

export async function listInvoices(signal?: AbortSignal): Promise<Invoice[]> {
  const res = await apiFetch("/billing/invoices", { auth: true, signal, schema: invoicesResponseSchema });
  return res.invoices;
}

/** Start a Stripe Checkout for the chosen paid tier; returns the hosted-checkout URL. */
export async function startCheckout(tier: PaidTierId): Promise<string> {
  const res = await apiFetch("/billing/checkout", {
    method: "POST",
    auth: true,
    body: { tier },
    schema: checkoutResponseSchema,
  });
  return res.url;
}

/** Navigate to the Stripe-hosted checkout. Isolated so screens can call it and tests
 *  can stub it (jsdom doesn't navigate). */
export function goToCheckout(url: string): void {
  window.location.assign(url);
}

/** Open the Stripe Billing Portal (self-serve cancel/downgrade/update-card, Phase 2);
 *  returns the hosted-portal URL. 400 (ApiError) if the org has never subscribed. */
export async function openBillingPortal(): Promise<string> {
  const res = await apiFetch("/billing/portal", { method: "POST", auth: true, schema: checkoutResponseSchema });
  return res.url;
}

/** Navigate to the Stripe-hosted Billing Portal. Isolated for testability. */
export function goToBillingPortal(url: string): void {
  window.location.assign(url);
}
